// @ts-strict-ignore
import React, { useState } from 'react';
import _ from 'lodash';
import { bindingsDefinition, injected, prop } from '@/hybrid/core/bindings.util';
import { useInjectedBindings } from '@/hybrid/core/hooks/useInjectedBindings.hook';
import { HoverTooltip } from '@/hybrid/core/HoverTooltip.atom';
import { IconWithSpinner } from '@/hybrid/core/IconWithSpinner.atom';
import { onEnterKeypress, onEventPreventPropagation } from '@/hybrid/core/onEnterKeypress.util';
import { Icon } from '@/hybrid/core/Icon.atom';
import { InvestigateActions } from '@/hybrid/toolSelection/investigate.actions';
import { ImportDatafileActions } from '@/hybrid/tools/importDatafile/importDatafile.actions';
import { SearchResultUtilitiesService } from '@/hybrid/search/searchResult.utilities.service';

import { pinToWorkbook, unpinToWorkbook } from '@/workbook/workbook.actions';
import { isAsset, isDatafile as isDatafileUtil, isDisplay as isDisplayUtil } from '@/hybrid/utilities/utilities';
import { errorToast } from '@/hybrid/utilities/toast.utilities';
import { TREND_TOOLS } from '@/hybrid/toolSelection/investigate.module';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { DisplayDropdown } from '@/hybrid/displays/DisplayDropdown.molecule';
import { sqSearchStore, sqWorkbookStore } from '@/core/core.stores';
import { doTrack } from '@/track/track.service';
import { fetchItemUsages } from '@/search/search.actions';

const searchResultIconsBindings = bindingsDefinition({
  item: prop<any>(),
  pinned: prop<any[]>(),
  workbookDisplay: prop<string>(),
  workbookId: prop<string>(),
  onItemClick: prop<(item, mode) => any>(),
  allowAssetReplacement: prop<boolean>(),
  restrictExploration: prop.optional<boolean>(),
  sqInvestigateActions: injected<InvestigateActions>(),
  sqImportDatafileActions: injected<ImportDatafileActions>(),
  sqSearchResultService: injected<SearchResultUtilitiesService>(),
});

export const SearchResultIcons: SeeqComponent<typeof searchResultIconsBindings> = (props) => {
  const { sqInvestigateActions, sqImportDatafileActions, sqSearchResultService } =
    useInjectedBindings(searchResultIconsBindings);

  const {
    item,
    pinned,
    workbookDisplay,
    workbookId,
    onItemClick,
    allowAssetReplacement,
    restrictExploration = false,
  } = props;

  const [isSwapping, setIsSwapping] = useState(false);
  const [isPinning, setIsPinning] = useState(false);

  const isDatafile = isDatafileUtil(item);
  const isDisplay = isDisplayUtil(item);
  const showInfoButton = isAsset(item) || isDatafile || item?.hasChildren;
  const showTrendIcon = !isAsset(item) && !isDatafile && !isDisplay && item?.hasChildren;
  const showSwapIcon = isAsset(item) || item?.hasChildren;
  const isLoadingUsages = sqSearchStore.searching === true;
  const isSearchingUsages = sqSearchStore.mode === 'usages';
  const isPinned = _.chain(pinned).map('id').includes(item.id).value();
  const isEdit = workbookDisplay === WORKBOOK_DISPLAY.EDIT;
  const displayParentProperties = () => {
    sqInvestigateActions.setActiveTool(TREND_TOOLS.PROPERTIES);
    sqInvestigateActions.setNonStoreItem(item);
  };

  const swapAsset = () => {
    doTrack('Search results', 'Swap Assets');
    setIsSwapping(true);

    sqSearchResultService
      .swapAsset(item)
      .catch((error) => errorToast({ httpResponseOrError: error, displayForbidden: true }))
      .finally(() => setIsSwapping(false));
  };

  const togglePin = () => {
    doTrack('Search results', 'pin to Workbook');
    const pinFunction = isPinned ? unpinToWorkbook : pinToWorkbook;
    setIsPinning(true);
    return pinFunction(workbookId, item.id).finally(() => setIsPinning(false));
  };

  const findUsages = () => {
    doTrack('Analysis', 'Usages', 'from search');
    return fetchItemUsages(item, 'main', sqWorkbookStore.workbookId ? [sqWorkbookStore.workbookId] : [], false);
  };

  return (
    <>
      {showInfoButton && (
        <span onClick={onEventPreventPropagation(displayParentProperties)}>
          <Icon
            icon="fa-info-circle"
            tooltip={isDatafile ? 'PROPERTIES.DATAFILE' : 'PROPERTIES.ASSET'}
            extraClassNames="searchBtn"
            testId="infoIcon"
          />
        </span>
      )}

      {showTrendIcon && (
        <HoverTooltip text="SEARCH_DATA.TREND_ITEM" placement="top">
          <span
            onClick={onEventPreventPropagation(() => isSwapping || onItemClick(item, sqSearchStore.mode))}
            onKeyUp={onEnterKeypress(() => isSwapping || onItemClick(item, sqSearchStore.mode))}>
            <IconWithSpinner
              icon="fa-area-chart"
              extraClassNames="searchBtn"
              spinning={isSwapping}
              testId="trendIcon"
              correctSpin={false}
            />
          </span>
        </HoverTooltip>
      )}

      {isDatafile && (
        <span
          onClick={onEventPreventPropagation(() => sqImportDatafileActions.loadDatafile(item.id))}
          onKeyUp={onEnterKeypress(() => sqImportDatafileActions.loadDatafile(item.id))}>
          <Icon
            icon="fa-pencil-square-o"
            tooltip="SEARCH_DATA.EDIT_CSV"
            extraClassNames="searchBtn"
            testId="editDatafileIcon"
          />
        </span>
      )}

      {isDisplay && (
        <DisplayDropdown
          item={item}
          isPinning={isPinning}
          isPinned={isPinned}
          togglePin={togglePin}
          restrictExploration={restrictExploration}
        />
      )}

      {isSearchingUsages && (
        <HoverTooltip text="SEARCH_DATA.USAGES">
          <span onClick={onEventPreventPropagation(() => findUsages())} onKeyUp={onEnterKeypress(() => findUsages())}>
            <IconWithSpinner
              icon="fc-usage"
              extraClassNames="searchBtn"
              testId="usagesIcon"
              spinning={isLoadingUsages}
              correctSpin={false}
            />
          </span>
        </HoverTooltip>
      )}

      {allowAssetReplacement && showSwapIcon && (
        <HoverTooltip text="ASSET_REPLACE.TOOLTIP" placement="top">
          <span
            onClick={onEventPreventPropagation(() => !isSwapping && swapAsset())}
            onKeyUp={onEnterKeypress(() => !isSwapping && swapAsset())}>
            <IconWithSpinner
              icon="fa-exchange"
              extraClassNames="searchBtn"
              spinning={isSwapping}
              testId="assetSwapIcon"
              correctSpin={false}
            />
          </span>
        </HoverTooltip>
      )}

      {isEdit && !isSearchingUsages && !isDisplay && (
        <HoverTooltip text={isPinned ? 'SEARCH_DATA.UNPIN' : 'SEARCH_DATA.PIN'}>
          <span
            onClick={onEventPreventPropagation(() => !isPinning && togglePin())}
            onKeyUp={onEnterKeypress(() => !isPinning && togglePin())}>
            <IconWithSpinner
              icon={isPinned ? 'fc-pin' : 'fc-pin-o'}
              extraClassNames="searchBtn"
              testId="pinIcon"
              spinning={isPinning}
              correctSpin={false}
            />
          </span>
        </HoverTooltip>
      )}
    </>
  );
};
